/**
 * Credit / Debit Notes adjustment workspace API client.
 *
 * Wraps every /credit-notes and /debit-notes endpoint (list, get, create,
 * preview, lifecycle transitions, dashboard). `api` already unwraps the
 * `{ status, data }` envelope to the inner `data` object, so each method reads
 * the named key off it.
 */
import { api } from './apiClient';

export type NoteKind = 'credit' | 'debit';
export type NoteStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'POSTED' | 'REVERSED' | 'CANCELLED';

export interface NoteLineInput {
  productId?: number | null;
  description?: string;
  quantity: number;
  unitPrice: number;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
  tdsAmount?: number;
  taxRate?: number | null;
  hsnCode?: string | null;
}

export interface CreateNoteInput {
  customerId?: number;          // credit
  vendorId?: number;            // debit
  invoiceId?: number | null;    // credit
  purchaseInvoiceId?: number | null; // debit
  cnDate?: string;              // credit doc date
  dnDate?: string;              // debit doc date
  postingDate?: string;
  noteType?: string;
  reference?: string;
  reason?: string;
  notes?: string;
  post?: boolean;               // false = create DRAFT
  lines: NoteLineInput[];
}

export interface NoteItem {
  id: number;
  productId?: number | null;
  description?: string | null;
  quantity: string | number;
  unitPrice: string | number;
  cgstAmount: string | number;
  sgstAmount: string | number;
  igstAmount: string | number;
  tdsAmount: string | number;
  taxRate?: string | number | null;
  hsnCode?: string | null;
  lineTotal: string | number;
}

export interface Note {
  id: number;
  cnNumber?: string;
  dnNumber?: string;
  customerId?: number;
  vendorId?: number;
  invoiceId?: number | null;
  purchaseInvoiceId?: number | null;
  cnDate?: string;
  dnDate?: string;
  postingDate?: string | null;
  reason?: string | null;
  noteType?: string | null;
  reference?: string | null;
  subtotal: string | number;
  cgstAmount: string | number;
  sgstAmount: string | number;
  igstAmount: string | number;
  totalAmount: string | number;
  appliedAmount?: string | number;
  status: NoteStatus;
  journalEntryId?: number | null;
  reversalJeId?: number | null;
  submittedBy?: number | null; submittedAt?: string | null;
  approvedBy?: number | null; approvedAt?: string | null;
  reversedBy?: number | null; reversedAt?: string | null;
  createdBy?: number | null;
  createdAt?: string;
  notes?: string | null;
  items?: NoteItem[];
  // enriched (from list/get joins, snake_case)
  party_name?: string | null;
  party_gstin?: string | null;
  invoice_number?: string | null;
  invoice_total?: string | number | null;
  invoice_paid?: string | number | null;
  journal_entry_number?: string | null;
  reversal_entry_number?: string | null;
}

export interface PreviewLeg {
  accountCode: string;
  accountName: string;
  debit: number;
  credit: number;
  description: string;
}

export interface NotePreview {
  legs: PreviewLeg[];
  totals: { subtotal: number; cgst: number; sgst: number; igst: number; tds: number; gstDelta: number; total: number };
  totalDebit: number;
  totalCredit: number;
  difference: number;
  balanced: boolean;
}

export interface NoteKindDashboard {
  periodCount: number;
  periodValue: number;
  pendingApproval: number;
  pendingPosting: number;
  taxImpact: number;
  totalValue: number;
  reversed: number;
}

export interface NotesDashboard {
  credit: NoteKindDashboard;
  debit: NoteKindDashboard;
  combined: { pendingApproval: number; pendingPosting: number; taxImpact: number; totalValue: number; reversed: number };
}

const base = (kind: NoteKind) => (kind === 'credit' ? '/credit-notes' : '/debit-notes');
const respKey = (kind: NoteKind) => (kind === 'credit' ? 'creditNote' : 'debitNote');
const listKey = (kind: NoteKind) => (kind === 'credit' ? 'creditNotes' : 'debitNotes');

export const notesService = {
  dashboard: (): Promise<NotesDashboard> => api.get('/credit-debit-notes/dashboard'),

  list: (kind: NoteKind, filters: Record<string, any> = {}): Promise<Note[]> =>
    api.get(base(kind), { params: filters }).then((r: any) => r[listKey(kind)] || []),

  get: (kind: NoteKind, id: number): Promise<Note> =>
    api.get(`${base(kind)}/${id}`).then((r: any) => r[respKey(kind)]),

  create: (kind: NoteKind, body: CreateNoteInput): Promise<Note> =>
    api.post(base(kind), body).then((r: any) => r[respKey(kind)]),

  preview: (kind: NoteKind, body: { lines: NoteLineInput[]; reference?: string }): Promise<NotePreview> =>
    api.post(`${base(kind)}/preview`, body).then((r: any) => r.preview),

  submit: (kind: NoteKind, id: number): Promise<Note> =>
    api.post(`${base(kind)}/${id}/submit`).then((r: any) => r[respKey(kind)]),

  approve: (kind: NoteKind, id: number): Promise<Note> =>
    api.post(`${base(kind)}/${id}/approve`).then((r: any) => r[respKey(kind)]),

  post: (kind: NoteKind, id: number): Promise<Note> =>
    api.post(`${base(kind)}/${id}/post`).then((r: any) => r[respKey(kind)]),

  reverse: (kind: NoteKind, id: number, reason?: string): Promise<Note> =>
    api.post(`${base(kind)}/${id}/reverse`, { reason }).then((r: any) => r[respKey(kind)]),
};

export default notesService;
